import type { BackendTypes } from '@tf/api';
import type { FormStatuses } from '@tf/shared';
import { createStyles, Group, Icon, type IconComponent, type Sx, TFText, Tooltip } from '@tf/ui';
import type React from 'react';

import { useSelectedReview } from '@/core/hooks';

interface Props {
	status: FormStatuses;
	isDecisionRequired: boolean;
}

const badgeStyles: Sx = {
	flexWrap: 'nowrap',
	borderRadius: 4,
	height: 36,
	minWidth: 36,
	padding: '0 12px',
	cursor: 'default',
	userSelect: 'none',
};

const useStyles = createStyles(({ colors, fontSizes }) => ({
	icon: {
		flexShrink: 0,
	},
	text: {
		fontWeight: 500,
		fontSize: '0.825rem',
	},
	valid: {
		backgroundColor: colors.brand[0],
		color: colors.brand[6],
		...badgeStyles,
	},
	verified: {
		backgroundColor: colors.green[0],
		color: colors.green[6],
		...badgeStyles,
	},
	errors: {
		backgroundColor: colors.red[0],
		color: colors.red[5],
		...badgeStyles,
	},
	pending: {
		backgroundColor: colors.gray[1],
		color: colors.gray[4],
		...badgeStyles,
	},
}));

const State: React.FC<{
	className: string;
	text: string;
	// Tabler v3 icons are ForwardRefExoticComponent, which a hand-written
	// ComponentType cannot accept via React's legacy propTypes field.
	iconComponent: IconComponent;
}> = ({ className, text, iconComponent: IconComponent }) => {
	const { classes } = useStyles();

	return (
		<Tooltip label={text}>
			<Group gap={6} className={className}>
				<IconComponent className={classes.icon} size={14} />
				<TFText className={classes.text} truncate="end">
					{text}
				</TFText>
			</Group>
		</Tooltip>
	);
};

export const FormStatusLabel: React.FC<Props> = ({ status, isDecisionRequired }) => {
	const { classes } = useStyles();
	const review = useSelectedReview();

	if (review.state !== 'DRAFT') {
		switch (status.review) {
			case 'ACCEPTED':
				return (
					<State iconComponent={Icon.IconCircleCheck} className={classes.verified} text="Form verified by Compliance" />
				);
			case 'REJECTED':
				return <State iconComponent={Icon.IconCircleX} className={classes.errors} text="Form rejected by Compliance" />;
			default:
				return isDecisionRequired ? (
					<State iconComponent={Icon.IconClock} className={classes.valid} text="Form awaits verification" />
				) : null;
		}
	}

	return <FormStatusLabelBase status={status.structure} />;
};

export const FormStatusLabelBase: React.FC<{ status?: BackendTypes.FieldValidationStatus }> = ({ status }) => {
	const { classes } = useStyles();

	switch (status) {
		case 'OK':
			return (
				<State
					iconComponent={Icon.IconCircleCheck}
					className={classes.valid}
					text="Form completed and ready for review"
				/>
			);
		case 'VALIDATION_ERRORS':
			return <State iconComponent={Icon.IconAlertCircle} className={classes.errors} text="Form contains errors" />;
		case 'PENDING_FILL':
			return (
				<State iconComponent={Icon.IconCircleDashed} className={classes.pending} text="Form waiting to be completed" />
			);
		default:
			return null;
	}
};
